import axios, { AxiosResponse } from 'axios';

export /**
 * @method get
 * @brief
 */
const getFeedDashboard = async ({
    startDate,
    endDate,
    centerId,
    accessToken,
}: GetFeedDashboardDataType & { accessToken: string }): Promise<any> => {
    let response = {} as AxiosResponse<any, any>;
    try {
        response = await axios({
            method: 'get',
            url: `${process.env.REACT_APP_SERVER}/v1/centers/feed-dashboard/${centerId}?startDate=${startDate}&endDate=${endDate}`,
            headers: { authorization: accessToken },
        });

        if (response.status === 200) {
            return response;
        }
    } catch (error: any) {
        // Error 😨
        switch (error.response.status) {
            case 401:
                return { status: error.response.status };
            case 403:
                return { status: error.response.status };
            case 500:
                alert('서버 내부 오류.');
                return { status: error.response.status };
        }
    }
};

export /**
 * @method get
 * @brief
 */
const getFeedDashboardCoachDetail = async ({
    startDate,
    endDate,
    centerId,
    coachId,
    accessToken,
}: GetFeedDashboardCoachDetailDataType & { accessToken: string }): Promise<any> => {
    let response = {} as AxiosResponse<any, any>;
    try {
        response = await axios({
            method: 'get',
            url: `${process.env.REACT_APP_SERVER}/v1/centers/feed-dashboard-detail/${centerId}/${coachId}?startDate=${startDate}&endDate=${endDate}`,
            headers: { authorization: accessToken },
        });

        if (response.status === 200) {
            return response;
        }
    } catch (error: any) {
        // Error 😨
        switch (error.response.status) {
            case 401:
                return { status: error.response.status };
            case 403:
                return { status: error.response.status };
            case 500:
                alert('서버 내부 오류.');
                return { status: error.response.status };
        }
    }
};

export type GetFeedDashboardDataType = {
    startDate: string;
    endDate: string;
    centerId: string;
    isNull: boolean;
};

export type GetFeedDashboardCoachDetailDataType = {
    startDate: string;
    endDate: string;
    centerId: string;
    coachId: string;
};

export type GetCenterCoachRankingDataType = {
    centerId: string;
    period: 'yearly' | 'monthly' | 'weekly' | 'daily';
    baseDate?: string;
    week?: number;
    day?: number;
};

export const getCenterCoachRanking = async ({
    centerId,
    period,
    baseDate,
    week,
    day,
    accessToken,
}: GetCenterCoachRankingDataType & { accessToken: string }): Promise<any> => {
    const params = new URLSearchParams({ period });
    if (baseDate) params.append('baseDate', baseDate);
    if (week != null) params.append('week', String(week));
    if (day != null) params.append('day', String(day));

    let response = {} as AxiosResponse<any, any>;
    try {
        response = await axios({
            method: 'get',
            url: `${process.env.REACT_APP_SERVER}/v1/centers/${centerId}/coach-ranking?${params.toString()}`,
            headers: { authorization: accessToken },
        });
        if (response.status === 200) return response;
    } catch (error: any) {
        switch (error.response?.status) {
            case 401:
                return { status: error.response.status };
            case 403:
                return { status: error.response.status };
            case 500:
                return { status: error.response.status };
        }
    }
};

export type ReRegistrationSuccessMemberStatus = 'SUCCEEDED' | 'NOT_YET_RE_REGISTERED';

export type GetReRegistrationSuccessMembersDataType = {
    centerId: string;
    cohortMonth: string;
    status?: ReRegistrationSuccessMemberStatus;
    search?: string;
    page?: number;
    limit?: number;
};

/**
 * 종료월 기준 재등록 성공률에 포함된 회원 목록을 조회한다.
 */
export const getReRegistrationSuccessMembers = async ({
    centerId,
    cohortMonth,
    status,
    search,
    page = 1,
    limit = 20,
    accessToken,
}: GetReRegistrationSuccessMembersDataType & { accessToken: string }): Promise<any> => {
    const params = new URLSearchParams({
        cohortMonth,
        page: String(page),
        limit: String(limit),
    });
    if (status) params.append('status', status);
    if (search?.trim()) params.append('search', search.trim());

    try {
        return await axios({
            method: 'get',
            url: `${process.env.REACT_APP_SERVER}/v1/centers/${centerId}/re-registration-success/members?${params.toString()}`,
            headers: { authorization: accessToken },
        });
    } catch (error: any) {
        const statusCode = error.response?.status;
        if ([400, 401, 403, 404, 500].includes(statusCode)) {
            return { status: statusCode };
        }
        throw error;
    }
};

/** 지표별 강사 순위 표의 지표 키. 백엔드 centerCoachEvidence.service.ts 와 1:1 대응한다. */
export type CenterCoachMetricKey =
    | 'revenue'
    | 'undeliveredRevenue'
    | 'avgPricePerClass'
    | 'totalClasses'
    | 'noShowCount'
    | 'sameDayCancelCount'
    | 'sameDayChangeCount'
    | 'journalWriteRate'
    | 'reviewAvg'
    | 'totalMembers'
    | 'newPayMembers'
    | 'reRegPayMembers'
    | 'reRegistrationRate'
    | 'reRegistrationSuccessRate';

/** all=분모 전체, included=분자에 포함된 건만, excluded=분모에만 포함된 건만 */
export type CenterCoachEvidenceFilter = 'all' | 'included' | 'excluded';

export type GetCenterCoachMetricEvidenceDataType = {
    centerId: string;
    coachId: string;
    metricKey: CenterCoachMetricKey;
    /** YYYY-MM */
    month: string;
    filter?: CenterCoachEvidenceFilter;
    page?: number;
    size?: number;
};

/**
 * 지표별 강사 순위 막대 1개의 원천 데이터(근거)를 조회한다.
 */
export const getCenterCoachMetricEvidence = async ({
    centerId,
    coachId,
    metricKey,
    month,
    filter = 'all',
    page = 1,
    size = 20,
    accessToken,
}: GetCenterCoachMetricEvidenceDataType & { accessToken: string }): Promise<any> => {
    const params = new URLSearchParams({
        month,
        filter,
        page: String(page),
        size: String(size),
    });

    try {
        return await axios({
            method: 'get',
            url: `${
                process.env.REACT_APP_SERVER
            }/v1/centers/${centerId}/coaches/${coachId}/metrics/${metricKey}/evidence?${params.toString()}`,
            headers: { authorization: accessToken },
        });
    } catch (error: any) {
        const statusCode = error.response?.status;
        if ([400, 401, 403, 404, 500].includes(statusCode)) {
            return { status: statusCode };
        }
        throw error;
    }
};
